import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { X, FileText, Globe, AlertCircle } from "lucide-react";

/**
 * Shows the user *exactly* what got extracted from a URL/file/text source.
 * Two trigger modes:
 *   1. `sourceId` prop → fetches GET /knowledge/source/:id/preview lazily
 *   2. `initialText` + `urls` props → shows immediately after a fresh crawl
 */
export default function ExtractedPreviewDialog({ open, onClose, sourceId, initialText, urls, title }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!open) return;
    if (initialText !== undefined) {
      setData({ name: title, text: initialText, total_chars: initialText.length, chunks: urls?.length || 1, type: "url", urls });
      return;
    }
    if (!sourceId) return;
    setLoading(true); setErr(null);
    api.get(`/knowledge/source/${sourceId}/preview`)
      .then(({ data }) => setData(data))
      .catch((e) => setErr(e?.response?.data?.detail || "Failed to load preview"))
      .finally(() => setLoading(false));
  }, [open, sourceId, initialText, urls, title]);

  if (!open) return null;

  const isUrl = data?.type === "url";

  return (
    <div className="fixed inset-0 z-[200] bg-black/60 flex items-center justify-center p-4" data-testid="extracted-preview-dialog">
      <div className="bg-white w-full max-w-3xl max-h-[85vh] flex flex-col rounded-sm shadow-2xl">
        <div className="flex items-center justify-between px-5 py-3 border-b-2 border-[#030712]">
          <div className="flex items-center gap-2 min-w-0">
            {isUrl ? <Globe className="w-4 h-4 text-[#0044FF] shrink-0" /> : <FileText className="w-4 h-4 text-[#0044FF] shrink-0" />}
            <div className="min-w-0">
              <div className="font-display font-extrabold text-sm uppercase tracking-tight">What your agent learned</div>
              <div className="text-[11px] text-[#4B5563] font-mono truncate">{data?.name || title || "—"}</div>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-[#E2E8F0]" data-testid="preview-close-button"><X className="w-4 h-4" /></button>
        </div>

        {loading && (
          <div className="flex-1 flex items-center justify-center text-xs text-[#4B5563]">
            <div className="w-5 h-5 border-2 border-[#0044FF]/30 border-t-[#0044FF] rounded-full animate-spin mr-2" /> loading extracted content…
          </div>
        )}

        {err && (
          <div className="flex-1 flex items-center justify-center p-6 text-center">
            <div>
              <AlertCircle className="w-8 h-8 text-[#EF4444] mx-auto mb-2" />
              <div className="text-sm font-medium">{err}</div>
            </div>
          </div>
        )}

        {data && !loading && (
          <>
            <div className="px-5 py-2 bg-[#F9FAFB] border-b border-[#E2E8F0] flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] font-mono text-[#4B5563]">
              <span><strong className="text-[#030712]">{data.total_chars?.toLocaleString() || 0}</strong> chars</span>
              <span><strong className="text-[#030712]">{data.chunks || 0}</strong> chunks embedded</span>
              {data.urls?.length > 1 && <span><strong className="text-[#030712]">{data.urls.length}</strong> pages crawled</span>}
              {data.truncated && <span className="text-[#F59E0B]">preview truncated to 20k chars</span>}
            </div>

            {data.urls?.length > 0 && (
              <div className="px-5 py-2 border-b border-[#E2E8F0] text-[10px] font-mono">
                <div className="text-[#4B5563] mb-1">extracted from:</div>
                <div className="space-y-0.5 max-h-20 overflow-auto">
                  {data.urls.map((u) => (
                    <div key={u} className="truncate">
                      <a href={u} target="_blank" rel="noreferrer" className="text-[#0044FF] hover:underline">{u}</a>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex-1 overflow-auto p-5">
              {data.text ? (
                <pre data-testid="preview-content" className="whitespace-pre-wrap font-mono text-[12px] leading-relaxed text-[#0F1115]">{data.text}</pre>
              ) : (
                <div className="text-center text-xs text-[#4B5563] py-8">No text content extracted.</div>
              )}
            </div>

            <div className="px-5 py-3 border-t border-[#E2E8F0] text-[11px] text-[#4B5563] bg-[#F9FAFB]">
              This is the exact text your agent will reference. If something important is missing, paste it as a Text source or upload a PDF/DOCX.
            </div>
          </>
        )}
      </div>
    </div>
  );
}
